import type { MenuItem } from '@/types/menuItem';
import type { ApiMenuOptionGroup, MenuAvailabilityInfo } from '@/lib/api/types';

// 옵션 타입 정의
export enum OptionType {
  REQUIRED_SINGLE = 'required_single',      // 필수 옵션 (정확히 1개)
  OPTIONAL_SINGLE = 'optional_single',      // 선택 옵션 (0개 또는 1개)
  MULTI_UNLIMITED = 'multi_unlimited',      // 멀티 선택 - 제한 없음
  MULTI_EXACT = 'multi_exact',             // 멀티 선택 - 정확히 N개
  MULTI_RANGE = 'multi_range',             // 멀티 선택 - 범위 (N~M개)
  TEXT_INPUT = 'text_input',               // 텍스트 입력
  NUMBER_INPUT = 'number_input',           // 숫자 입력
  TEXTAREA = 'textarea'                    // 텍스트 영역 입력
}

// 옵션 인터페이스 정의
export interface MenuOption {
  id: string;
  nameKey: string;
  descriptionKey?: string;
  price: number;
  type: OptionType;
  minSelection?: number;
  maxSelection?: number;
  exactSelection?: number;
  options: {
    id: string;
    nameKey: string;
    description?: string;
    additionalPrice: number;
    /** Maximum quantity of this choice in one customer order. Null means no per-choice limit. */
    quantityLimit?: number | null;
    /** API 직접 이름 (번역 키 대신 사용) */
    _name?: string;
  }[];
  /** API 직접 이름 (번역 키 대신 사용) */
  _name?: string;
}

/**
 * 메뉴 아이템 + 옵션 그룹 (옵션 모달 전용).
 *
 * #289: `MenuItem`을 extends 하여 공통 필드(availability, discount, isSoldOut 등)의
 * silent drift를 타입 시스템에서 차단. 옵션 모달 고유 필드(`maxQuantity`, `options`)만 추가.
 */
export interface MenuItemWithOptions extends MenuItem {
  maxQuantity?: number;
  options?: MenuOption[];
}

// 선택된 옵션의 수량 정보를 저장하는 인터페이스
export interface SelectedOptionQuantity {
  optionId: string;
  quantity: number;
}

/**
 * 장바구니/주문에서 사용되는 선택된 옵션 정보
 * API와 로컬 데이터 모두 호환
 */
export interface SelectedOption {
  /** 옵션 ID */
  id: string;
  /** 옵션 그룹 ID */
  groupId?: string;
  /** API 옵션 이름 (우선 사용) */
  _name?: string;
  /** 로컬 옵션 이름 또는 번역 키 */
  name?: string;
  /** API 그룹 이름 */
  _groupName?: string;
  /** 로컬 그룹 이름 */
  groupName?: string;
  /** 추가 가격 */
  additionalPrice?: number;
  /** 수량 (수량 제어 옵션용) */
  quantity?: number;
  /**
   * #496 hotfix (사후 codex P1-2): 입력형 옵션 사용자 입력 값.
   * BE 응답의 input_value 필드에 매핑 — number_input/text_input/textarea 의
   * 사용자 입력값을 cart/order 흐름 전체에서 보존 + BE 전송.
   */
  input_value?: string;
}

/**
 * 장바구니에 추가할 아이템 (MenuItemWithOptions + CartItem 공통 필드)
 * 기존 코드 호환성을 위해 유연하게 정의
 */
export interface CartItemInput {
  id: string;
  name?: string;
  nameKey?: string;
  description?: string;
  descriptionKey?: string;
  price: number;
  originalPrice?: number;
  imageUrl?: string;
  category?: string;
  restaurantId?: string;
  restaurantName?: string;
  customizations?: string[];
  selectedOptions?: Record<string, string[]>;
  /**
   * #498: 입력형 옵션 (number_input/text_input/textarea) 사용자 입력 값.
   * cart 편집 / sessionStorage 직렬화 / BE 주문 전송 모두에서 보존.
   */
  inputValues?: Record<string, string>;
  isAvailable?: boolean;
  /** API 직접 이름 */
  _name?: string;
  /** API 직접 설명 */
  _description?: string;
  /** 원본 API 옵션 데이터 (장바구니 변경 버튼에서 옵션 편집용) */
  _originalOptions?: ApiMenuOptionGroup[];
  /** #280: 가용성 정보 — 장바구니 편집 모달에서 판매 시간 행 노출용 */
  _availability?: MenuAvailabilityInfo;
}

/**
 * onAddToCart 콜백 함수 타입
 */
export type OnAddToCartCallback = (
  item: CartItemInput,
  quantity: number,
  selectedOptions: SelectedOption[]
) => void;

/**
 * onUpdateCartItemOptions 콜백 함수 타입
 */
export type OnUpdateCartItemOptionsCallback = (
  itemId: string,
  updatedItem: CartItemInput | null,
  quantity: number,
  selectedOptions: SelectedOption[]
) => void;
